import { TranscribedWord } from "@giga-user-fern/api/types/api/resources/guides";
import { SubtitlesSettings } from "@giga-user-fern/api/types/api/resources/video";

export interface RendererSubtitle {
	startTime: number;
	endTime: number;
	text: string;
}

export interface TranscribedWordPosition {
	word: string;
	line: number;
	startIndex: number;
	endIndex: number;
	startTime: number;
	endTime: number;
}

export const DEFAULT_SUBTITLES: SubtitlesSettings = {
	showSubtitles: false,
	size: 62, //ideal size for 1920 x 1080 videos
};

export const parseTimeToSeconds = (timeStr: string): number => {
	const [hours, minutes, seconds] = timeStr.split(":");
	const [secs, ms] = seconds.split(",");
	return (
		Number.parseInt(hours) * 3600 +
		Number.parseInt(minutes) * 60 +
		Number.parseInt(secs) +
		Number.parseInt(ms) / 1000
	);
};

/**
 * Get all transcribed words that fall within the current subtitle's time range
 */
export const getSubtitleWords = (
	transcribedWords: TranscribedWord[],
	subtitle: RendererSubtitle | null,
): TranscribedWord[] => {
	if (!subtitle || !transcribedWords.length) return [];

	return transcribedWords.filter(
		(word) =>
			subtitle &&
			Math.round(word.start * 100) / 100 >=
				Math.round(subtitle.startTime * 100) / 100 &&
			Math.round(word.end * 100) / 100 <=
				Math.round(subtitle.endTime * 100) / 100,
	);
};

/**
 * Map words in a subtitle to their positions within each line
 */
export const mapWordsToPositions = (
	subtitleWords: TranscribedWord[],
	lines: string[],
): TranscribedWordPosition[] => {
	const wordPositions: TranscribedWordPosition[] = [];

	if (subtitleWords.length === 0 || lines.length === 0) {
		return wordPositions;
	}

	// Create a combined version of all lines for full-text matching
	const fullSubtitleText = lines.join(" ").toLowerCase();

	// Sort words by their start time
	const sortedWords = [...subtitleWords].sort((a, b) => a.start - b.start);

	// For each transcribed word, try to find its position in the subtitle
	let lastIndex = 0;
	for (const word of sortedWords) {
		const wordText = (word.punctuatedWord || word.word).toLowerCase();
		// Start searching from last found position to maintain order
		const index = fullSubtitleText.indexOf(wordText, lastIndex);

		if (index !== -1) {
			// Now determine which line this word belongs to
			let charCount = 0;
			let lineIndex = 0;

			for (let i = 0; i < lines.length; i++) {
				const lineLength = lines[i].length;

				if (index >= charCount && index < charCount + lineLength + 1) {
					// +1 for the space we added
					// Found the line
					lineIndex = i;
					const startIndexInLine = index - charCount;

					wordPositions.push({
						word: wordText,
						line: lineIndex,
						startIndex: startIndexInLine,
						endIndex: startIndexInLine + wordText.length,
						startTime: word.start,
						endTime: word.end,
					});

					break;
				}

				// Add line length plus a space for the joined text
				charCount += lineLength + 1;
			}

			// Move past this word for next search
			lastIndex = index + wordText.length;
		}
	}

	return wordPositions;
};

/**
 * Find the word that should be highlighted on a specific line at the current time
 */
export const findWordToHighlight = (
	wordPositions: TranscribedWordPosition[],
	lineIndex: number,
	currentTime: number,
): TranscribedWordPosition | null => {
	return (
		wordPositions.find(
			(pos) =>
				pos.line === lineIndex &&
				currentTime >= pos.startTime &&
				currentTime <= pos.endTime,
		) || null
	);
};

export const parseSubtitles = (srtString: string): RendererSubtitle[] => {
	/**
	 * @input srtString: a stringified SRT file.
	 * @output subtitles: an array of subtitles that can be used in the renderer.
	 */

	const subtitles: RendererSubtitle[] = [];
	const lines = srtString.split(/\r?\n/); // Properly split by lines
	let i = 0;

	while (i < lines.length) {
		// Step 1: Parse index (skip empty lines)
		while (i < lines.length && !lines[i].trim()) {
			i++; // Skip blank lines
		}
		if (i >= lines.length) break;

		const index = Number.parseInt(lines[i], 10);
		if (Number.isNaN(index)) {
			i++; // If it's not a valid number, move on
			continue;
		}

		i++; // Move to timestamp line
		if (i >= lines.length) break;

		// Step 2: Parse time range
		const timeRange = lines[i].split(" --> ");
		if (timeRange.length !== 2) {
			i++;
			continue;
		}
		const startTime = parseTimeToSeconds(timeRange[0]);
		const endTime = parseTimeToSeconds(timeRange[1]);

		i++; // Move to subtitle text
		if (i >= lines.length) break;

		// Step 3: Collect text until we hit a new index or run out of lines
		let text = "";
		while (
			i < lines.length &&
			!Number.isInteger(Number.parseInt(lines[i], 10)) && // Stop at a new index
			!lines[i].includes("-->") // Stop at a timestamp
		) {
			text += lines[i] + " ";
			i++;
		}

		subtitles.push({
			startTime,
			endTime,
			text: text.trim(),
		});
	}

	return subtitles;
};
